import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, throwError, timeout } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LocalStorageService } from './local-storage.service';
import { environment } from 'src/environments/environment';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { QR_LOGIN } from 'src/constants/storage-keys';
import { BookingMode } from 'src/shared/models/booking-mode/booking-mode';
import { Network } from '@capacitor/network';
import { AuthenticationData } from 'src/shared/models/authentication-data';
import { jwtDecode } from "jwt-decode";
import { UserMode } from './tabs/tabs-meta';
import { AuthProvider } from './auth.service';
import { PermissionService } from './permission.service';
import { StorageProvider } from './storage.provider';
import * as fp from 'lodash/fp';

enum RequestType {
  Get = 1,
  Post,
  Put,
  Delete,
}

export enum OfflineWrapResult {
  Error, Success, Offline
}

interface WrapIntoOfflineParams<T> {
  fetch: Function,
  params?: Array<any>,
  storeKey: string,
  renew?: boolean,
  type?: T
}

interface RequestOptions {
  headers?: HttpHeaders | { [header: string]: string | string[]; };
}

@Injectable({ providedIn: 'root' })
export class AuthenticatedHttpService {
  static readonly AUTHENTICATION_DATA: string = "AUTHENTICATION_DATA";

  public readonly _isOffline = new BehaviorSubject<boolean>(false); // That was first low-level logic
  public readonly isOffline$ = this._isOffline.asObservable(); // Use Session Service

  public readonly canRefreshToken = new BehaviorSubject<boolean>(true);

  initRequired: boolean;

  readonly AUTHORIZATION: string = "Authorization";
  readonly CONTENT_TYPE: string = "Content-Type";
  readonly APPLICATION_JSON: string = "application/json";

  private authData: AuthenticationData | null;

  private _apiHealth = false;
  private _apiIdentityHealth = false;

  get userId() {
    return fp.get("jwt.sub", this.authData);
  }

  constructor(
    private http: HttpClient,
    private storage: LocalStorageService,
    private permissionService: PermissionService,
    private storageProvider: StorageProvider,
    private authProvider: AuthProvider,
  ) {
    this.runApiHealthCheck();
  }

  async initializeAuthViaCode(authorizationCode: string, finishedCallback: () => void, errorCallback: () => void) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const body = new HttpParams()
      .set('client_id', environment.web.clientId)
      .set('client_secret', environment.web.clientSecret)
      .set('grant_type', 'authorization_code')
      .set('code', authorizationCode)
      .set('redirect_uri', environment.web.redirect);

    try {
      await this.auth(body.toString(), { headers }, finishedCallback, errorCallback, `initializeAuth error, redirect uri: ${environment.web.redirect}`);
    }
    catch (error) {
      console.error('Error in initializeAuthViaCode:', error);

      errorCallback();
    }
  }
  async initializeAuth(username: string, password: string, finishedCallback: () => void, errorCallback: () => void) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const body = new HttpParams()
      .set('client_id', 'rota')
      .set('grant_type', 'password')
      .set('username', username)
      .set('password', password);

    try {
      await this.auth(body.toString(), { headers }, finishedCallback, errorCallback, `initializeDesktopAuth error, redirect uri: ${environment.web.redirect}`);
    }
    catch (error) {
      console.error('Error in initializeAuth:', error);

      errorCallback();
    }
  }

  private async auth(body: any, options: any, finishedCallback: () => void, errorCallback: () => void, tmpError: string) {
    try {
      const response: any = await this.http.post<any>(`${environment.web.identity}/connect/token`, body, options)
        .pipe(
          timeout(30 * 1000), // Timeout after 30 seconds
          catchError(error => {
            console.error('Error in auth request:', error);
            return throwError(error); // Re-throw the error
          })
        )
        .toPromise();

      this.authData = new AuthenticationData();
      this.authData.set(response.token_type, response.access_token, response.refresh_token, jwtDecode(response.access_token));
      await this.storage.set(AuthenticatedHttpService.AUTHENTICATION_DATA, this.authData);

      await this.permissionService.init(this.authData);

      finishedCallback();
    } catch (error) {
      console.error(tmpError, error);
      errorCallback();

      if (HttpUtils.isNotAuthenticatedError(error)) {
        this.authProvider.logoutEvent();
      }
    }
  }

  // Returns true if session is alive
  // false if offline and haven't been checked yet
  // throws if session is dead
  async refreshToken(): Promise<boolean> {
    this.canRefreshToken.next(false);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    let body = '';

    try {
      body = await this.getBodyForRefreshToken();

      const response = await HttpUtils.handle(
        { request: this.http.post(environment.web.identity + "/connect/token", body, { headers }).toPromise() }
      );

      this.authData = new AuthenticationData();
      this.authData.set(response.token_type, response.access_token, response.refresh_token, jwtDecode(response.access_token));
      this.storage.set(AuthenticatedHttpService.AUTHENTICATION_DATA, this.authData);
      await this.permissionService.init(this.authData);

      this._isOffline.next(false);
      this.initRequired = false;
      setTimeout(() => this.canRefreshToken.next(true), 5000);

      return true;

    } catch (error) {
      console.error('refreshToken error:', error);

      setTimeout(() => this.canRefreshToken.next(true), 5000);
      this._isOffline.next(false);
      this.initRequired = false;

      if (HttpUtils.isTimeout(error) && this.authData != null) {
        await this.permissionService.init(this.authData);
        this._isOffline.next(true);
        this.initRequired = true;
        return false;
      }

      if (HttpUtils.isNotAuthenticatedError(error) || HttpUtils.isBadRequest(error) || this.authData == null) {
        this.clearAuth();
        this.authProvider.logoutEvent();
      }

      return false;
    }
  }

  private async getBodyForRefreshToken(): Promise<string> {
    if (!this.authData) {
      this.authProvider.logoutEvent();
      return '';
    }

    if (this.storageProvider.isDesktopPlatform) {
      console.log("get refresh for desktop");
      return 'client_id=' + encodeURIComponent("rota") +
        '&grant_type=' + encodeURIComponent("refresh_token") +
        '&refresh_token=' + encodeURIComponent(this.authData.refreshToken) +
        '&redirect_uri=' + encodeURIComponent(environment.web.redirect);
    }

    if (await this.storage.get(QR_LOGIN)) {
      console.log("get refresh for mobile with qr");
      return 'client_id=' + encodeURIComponent("rota") +
        '&grant_type=' + encodeURIComponent("refresh_token") +
        '&refresh_token=' + encodeURIComponent(this.authData.refreshToken) +
        '&redirect_uri=' + encodeURIComponent(environment.web.redirect);
    }
    else {
      console.log("get refresh for mobile without qr ");
      return 'client_id=' + encodeURIComponent(environment.web.clientId) +
        '&client_secret=' + encodeURIComponent(environment.web.clientSecret) +
        '&grant_type=' + encodeURIComponent("refresh_token") +
        '&refresh_token=' + encodeURIComponent(this.authData.refreshToken) +
        '&redirect_uri=' + encodeURIComponent(environment.web.redirect);
    }
  }

  getUserName(): string {
    return fp.getOr("", "jwt.preferred_username", this.authData);
  }

  getUserDatabaseIdentifier(): string {
    return fp.getOr("", "jwt.DatabaseIdentifier", this.authData);
  }

  getAuthToken(): string {
    if (this.authData == null) {
      return "";
    }

    return this.authData.getToken();
  }

  getBookingMode(): UserMode {
    if (!this.authData) return UserMode.Unknown;

    const mode = this.authData.jwt.BookingMode as BookingMode;

    if (mode === BookingMode.MultiplePersons) {
      return UserMode.MultiUser;
    }

    return UserMode.SingleUser;
  }

  async clearAuth() {
    this.authData = null;
    await this.storage.remove(AuthenticatedHttpService.AUTHENTICATION_DATA);
  }

  async wrapIntoOffline<R>({
    fetch,
    params = [],
    storeKey,
    renew = true,
  }: WrapIntoOfflineParams<R>): Promise<{ status: OfflineWrapResult, result: R | undefined | null, error: any }> {
    let status = OfflineWrapResult.Error;
    let result: R | undefined | null = null;
    let error: any = null;

    try {
      result = await HttpUtils.handle<R>({ request: fetch.apply(this, params) });
      if (renew) {
        await this.storage.set(storeKey, result);
      }
      status = OfflineWrapResult.Success;
    } catch (err) {
      if (HttpUtils.isTimeout(err)) {
        try {
          const stored = await this.storage.get<R>(storeKey);
          result = stored || null;
          status = OfflineWrapResult.Offline;
        } catch (err) {
          error = err;
        }
      } else {
        error = err;
      }
    }

    return { status, result, error };
  }

  async get(url: string, options?: RequestOptions): Promise<any> {
    return await this.executeRequest(RequestType.Get, url, options);
  }
  async put(url: string, data: any, options?: RequestOptions): Promise<any> {
    return await this.executeRequest(RequestType.Put, url, options, data);
  }
  async delete(url: string, options?: RequestOptions): Promise<any> {
    return await this.executeRequest(RequestType.Delete, url, options);
  }
  async post(url: string, data: any, options?: RequestOptions): Promise<any> {
    return await this.executeRequest(RequestType.Post, url, options, data);
  }

  private async executeRequest(type: RequestType, url: string, options?: RequestOptions, data?: any, isRetry = false): Promise<any> {
    try {
      // Check and initialize authentication data
      if (await this.initAndGetAuthData()) {
        var network = await Network.getStatus();
        // Check network connectivity and token validity or refresh if possible
        if (network.connected && (!this.tokenExpired(this.authData!.accessToken)
          || (this.canRefreshToken.value && await this.refreshToken()))) {
          // Execute the request
          const res = await this.executeBareRequest(type, url, options, data, isRetry);
          this._isOffline.next(false);
          return res;
        }
        throw { status: 0 };
      } else {
        // Handle authentication data not found
        this.authProvider.logoutEvent();
        throw new Error("Auth data not found");
      }
    } catch (error) {
      // Handle timeout and log other errors
      if (HttpUtils.isTimeout(error)) {
        this._isOffline.next(true);
      }

      this.logError(error, url, data, type, isRetry);
      throw error;
    }
  }

  tokenExpired(token: string) {
    const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;
    return (Math.floor((new Date).getTime() / 1000)) >= expiry;
  }

  async executeBareRequest(type: RequestType, url: string, options?: RequestOptions, data?: any, isRetry = false): Promise<any> {
    options = this.getRequestOptions(url, options);

    try {
      let query: Observable<any>;

      switch (type) {
        case RequestType.Get:
          query = this.http.get(url, options);
          break;
        case RequestType.Post:
          query = this.http.post(url, data, options);
          break;
        case RequestType.Put:
          query = this.http.put(url, data, options);
          break;
        case RequestType.Delete:
          query = this.http.delete(url, options);
          break;
        default:
          throw new Error('Unsupported request type');
      }

      return await query.pipe(timeout(20000)).toPromise();
    } catch (error) {
      if (!isRetry && (HttpUtils.isNotAuthenticatedError(error)
        || HttpUtils.isForbidden(error)
        || HttpUtils.isTimeout(error))) {
        try {
          return await this.executeRequest(type, url, options, data, true);
        } catch (error) {
          throw error;
        }
      }

      throw error;
    }
  }

  private async runApiHealthCheck() { // Thats a background task that should only be run once at a time
    setTimeout(async () => {
      const network = await Network.getStatus();
      if (network.connected) {
        if (this._isOffline.value && this.authProvider.loggedIn) {
          const isOnline = await this.healthCheck();
          this._isOffline.next(!isOnline);
        }
      }
      else {
        this._isOffline.next(true);
      }

      this.runApiHealthCheck();
    }, 3 * 1000);
  }

  async healthCheck() {
    if (!this._apiHealth && !this._apiIdentityHealth) { // check both API
      await Promise.all([this.checkApi(false), this.checkApiIdentity(false)]);
    } else if (!this._apiHealth) { // check Identity API
      await this.checkApi(true);
    } else if (!this._apiIdentityHealth) { // check API
      await this.checkApiIdentity(true);
    }

    let isHealthBothApi = this._apiHealth && this._apiIdentityHealth;
    if (isHealthBothApi) { // reset the status of two API for the next time
      this._apiHealth = false;
      this._apiIdentityHealth = false;
    }

    return isHealthBothApi;
  }

  public async checkApi(isCheckIdentityApi: boolean): Promise<void> {
    try {
      const timeoutPromise = new Promise<void>((resolve, reject) => {
        setTimeout(() => reject('Request timeout'), 5000);
      });

      const apiRequest = this.http.get(`${environment.web.apiUrl}api/healthcheck`).toPromise();

      await Promise.race([apiRequest, timeoutPromise]);

      this._apiHealth = true;

      if (isCheckIdentityApi) {
        await this.checkApiIdentity(false);
      }

    } catch (error) {
      this._apiHealth = false;
      console.error('API health check failed:', error);
    }
  }

  public async checkApiIdentity(isCheckApi: boolean): Promise<void> {
    try {
      const timeoutPromise = new Promise<void>((resolve, reject) => {
        setTimeout(() => reject('Request timeout'), 5000); // timeout 5 sec
      });

      const apiRequest = this.http.get(`${environment.web.identity}/api/healthcheck`).toPromise();

      await Promise.race([apiRequest, timeoutPromise]);

      this._apiIdentityHealth = true;

      if (isCheckApi) {
        await this.checkApi(false);
      }

    } catch (error) {
      this._apiIdentityHealth = false;
      console.error('Identity API health check failed:', error);
    }
  }

  async initAndGetAuthData() {
    if (!this.authData || !fp.get("accessToken", this.authData)) {
      const storageAuthData = await this.storage.get(AuthenticatedHttpService.AUTHENTICATION_DATA);
      if (storageAuthData && fp.get("accessToken", storageAuthData)) {
        this.authData = new AuthenticationData();
        this.authData.copy(storageAuthData);
      }
      else {
        this.authData = null;
        return false;
      }
    }

    return true;
  }

  private logError(error: any, url: string, data: any, type: RequestType, isRetry: boolean) {
    // var errorLevel: Raven.LogLevel = "error";

    // if (HttpUtils.isTimeout(error)) {
    //   console.error("Timeout: ", error);
    //   return;
    // }
    // else if (HttpUtils.isBadRequest(error)) {
    //   console.error("Bad request: ", error);
    //   return;
    // }

    // const formattedError = `${(error.status || "").toString()}:${HttpUtils.stringifyError(error, "")}`
    // try {
    //   Raven.captureMessage(formattedError, {
    //     extra: {
    //       "user": this.getUserName(),
    //       "error": error,
    //       "url": url,
    //       "authData": this.authData
    //     },
    //     level: errorLevel
    //   });
    //   console.error(
    //     isRetry ? "Retry. " : "",
    //     `HttpError: ${RequestType[type]} ${url}`,
    //     ", data: ", data,
    //     formattedError, error
    //   );
    // } catch (e) {
    //   console.error("error can not be parced", error, e);
    // }
  }

  getRequestOptions(url: string, options?: RequestOptions): RequestOptions {
    if (url.startsWith('https://maps.googleapis.com') || url.startsWith('https://nominatim.openstreetmap.org')) {
      return options!;
    }

    if (this.authData != null && this.authData.getAuthHeader() != null && this.authData.getAuthHeader() != "") {
      let headers: HttpHeaders = new HttpHeaders({
        [this.AUTHORIZATION]: this.authData.getAuthHeader(),
        [this.CONTENT_TYPE]: this.APPLICATION_JSON
      });

      if (options && options.headers) {
        if (options.headers instanceof HttpHeaders) {
          headers = headers.delete(this.AUTHORIZATION);
          headers = headers.append(this.AUTHORIZATION, this.authData.getAuthHeader());

          if (!options.headers.has(this.CONTENT_TYPE)) {
            headers = headers.append(this.CONTENT_TYPE, this.APPLICATION_JSON);
          }
        } else {
          const customHeaders = options.headers as { [header: string]: string | string[] };
          Object.keys(customHeaders).forEach(header => {
            const value = customHeaders[header];
            if (Array.isArray(value)) {
              headers = headers.set(header, value.join(','));
            } else {
              headers = headers.set(header, value);
            }
          });

          if (!headers.has(this.AUTHORIZATION)) {
            headers = headers.set(this.AUTHORIZATION, this.authData.getAuthHeader());
          }

          if (!headers.has(this.CONTENT_TYPE)) {
            headers = headers.set(this.CONTENT_TYPE, this.APPLICATION_JSON);
          }
        }
      }

      options = { ...options, headers };
    }

    return options!;
  }
}